import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Heart, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { DEV_BACKEND_BASE_URL } from '../config/devBackendUrl';
import { treeHref } from '../utils/prettyArchiveLinks';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || DEV_BACKEND_BASE_URL;
const API = `${BACKEND_URL}/api`;

const BillingSuccess = () => {
  const location = useLocation();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('checking');
  const [detail, setDetail] = useState(null);
  const [archiveTreeId, setArchiveTreeId] = useState(null);

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const stripeSessionId = params.get('session_id');
  const internalSessionId = params.get('internal_session_id');
  const provider = params.get('provider');

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!stripeSessionId && !internalSessionId) {
        setStatus('error');
        setDetail('Missing session information.');
        return;
      }

      if (provider === 'mock' && internalSessionId) {
        try {
          await axios.post(`${API}/billing/checkout/confirm/${internalSessionId}`);
          await refreshUser?.();
          try {
            const { data: me } = await axios.get(`${API}/billing/me`);
            if (!cancelled && me?.default_archive_tree_id) {
              setArchiveTreeId(me.default_archive_tree_id);
            }
          } catch {
            /* optional */
          }
          if (!cancelled) {
            setStatus('done');
          }
        } catch (e) {
          toast.error(e?.response?.data?.detail || 'Unable to confirm checkout');
          if (!cancelled) setStatus('error');
        }
        return;
      }

      const poll = async () => {
        for (let i = 0; i < 30; i += 1) {
          if (cancelled) return;
          try {
            const { data } = await axios.get(`${API}/billing/checkout/session`, {
              params: stripeSessionId
                ? { stripe_session_id: stripeSessionId }
                : { internal_session_id: internalSessionId },
            });
            if (data.stripe_payment_status === 'paid' && data.local_status !== 'paid' && data.internal_session_id) {
              try {
                await axios.post(`${API}/billing/checkout/confirm/${data.internal_session_id}`);
              } catch {
                /* webhook may still process */
              }
            }
            if (data.local_status === 'paid' || data.stripe_payment_status === 'paid') {
              if (data.tree_id) setArchiveTreeId(data.tree_id);
              await refreshUser?.();
              if (!cancelled) setStatus('done');
              return;
            }
          } catch (e) {
            if (!cancelled) {
              setStatus('error');
              setDetail(e?.response?.data?.detail || 'Unable to verify payment');
            }
            return;
          }
          await new Promise((r) => setTimeout(r, 1500));
        }
        if (!cancelled) {
          setStatus('pending');
          setDetail('Payment is still processing. You will receive access when it completes.');
        }
      };

      await poll();
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [stripeSessionId, internalSessionId, provider, refreshUser]);

  useEffect(() => {
    if (status !== 'done') return undefined;
    let cancelled = false;
    const loadArchive = async () => {
      try {
        const { data } = await axios.get(`${API}/billing/me`);
        if (!cancelled && data?.default_archive_tree_id) {
          setArchiveTreeId(data.default_archive_tree_id);
        }
      } catch {
        /* keep tree from checkout session if /me fails */
      }
    };
    loadArchive();
    return () => {
      cancelled = true;
    };
  }, [status]);

  return (
    <div className="min-h-screen bg-[#faf8f5] text-[#1c1917]">
      <main className="mx-auto max-w-lg px-4 py-16 text-center sm:px-6">
        {status === 'checking' && (
          <>
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-[#8b7355]" />
            <p className="mt-4 text-sm text-[#5c534a]">Confirming your archive purchase…</p>
          </>
        )}
        {status === 'done' && (
          <>
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#e8dfd4]">
              <Heart className="h-7 w-7 text-[#8b5a3c]" fill="currentColor" aria-hidden />
            </div>
            <CheckCircle2 className="mx-auto mt-3 h-10 w-10 text-[#5c4a32]" aria-hidden />
            <h1 className="mt-4 font-serif text-2xl text-[#1c1917]">Your story has a permanent home</h1>
            <p className="mt-3 text-sm leading-relaxed text-[#5c534a]">
              This is a quiet celebration: the stories you treasure are protected here, ready for the people you love
              today and the generations who will wonder about you tomorrow.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
              {archiveTreeId ? (
                <Link to={treeHref({ id: archiveTreeId })}>
                  <Button className="h-12 w-full rounded-full bg-[#1c1917] text-[#faf7f2] hover:bg-[#2c2824] sm:min-w-[220px]">
                    Open your family archive
                  </Button>
                </Link>
              ) : (
                <Link to="/dashboard">
                  <Button className="h-12 w-full rounded-full bg-[#1c1917] text-[#faf7f2] hover:bg-[#2c2824] sm:min-w-[220px]">
                    Go to dashboard
                  </Button>
                </Link>
              )}
              <Link to="/dashboard">
                <Button variant="outline" className="h-12 w-full rounded-full border-[#c9a961]/40 sm:w-auto">
                  {archiveTreeId ? 'Dashboard' : 'Account home'}
                </Button>
              </Link>
            </div>
            <p className="mt-6 text-xs text-[#78716c]">
              Tip: open your archive to add stories, photos, and voices while the moment is fresh.
            </p>
          </>
        )}
        {status === 'pending' && (
          <>
            <p className="text-sm text-[#5c534a]">{detail}</p>
            <Link to="/dashboard" className="mt-6 inline-block text-sm font-medium text-[#5c4a32] underline">
              Continue to dashboard
            </Link>
          </>
        )}
        {status === 'error' && (
          <>
            <p className="text-sm text-[#5c534a]">{detail || 'Something went wrong.'}</p>
            <Link to="/pricing">
              <Button variant="outline" className="mt-6 rounded-full border-[#c9a961]/40">
                Return to pricing
              </Button>
            </Link>
          </>
        )}
      </main>
    </div>
  );
};

export default BillingSuccess;
